import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { DEFAULT_ADDRESS_RADIUS } from 'constants/settings';
import { SET_MAIN_PAGE_SEARCH } from 'store/mainPageSearch';
import { FREIGHTS } from 'helpers/routes';
import { FreightsService } from 'service/FreightsService';

import SearchForm from 'components/FreightComponents/SearchForm';
import SearchOptions from 'components/FreightComponents/SearchOptions';
import QuickSearchBar from 'components/FreightComponents/QuickSearchBar';
import SearchTabs from 'components/FreightComponents/SearchTabs';
import FreightsTable from 'components/FreightComponents/FreightsTable';
import FreightSearchModel from 'models/freightSearch';
import {
  MobileStatusFilters,
  RefreshButton,
  SearchFormButtons,
  searchPageStyles as styles,
} from 'components/common/SearchPage';
import { searchValidateSchema } from './validate';
import { RootState } from '../types/global';
import { useSearchQueryParams } from '@/hooks/useSearchQueryParams';
import { useNewItemsNotification } from '@/hooks/useNewItemsNotification';
import {
  urlStatusToInternal,
  internalStatusToUrl,
  getStatusesForFilter,
} from '@/utils/searchStatusUtils';

interface FreightSearchData {
  items: any[];
  count: number;
  nextCursor?: string;
}

interface SearchParams {
  [key: string]: any;
}

interface FormErrors {
  [key: string]: string;
}

interface LocationState {
  templateId?: string;
}

const ITEMS_PER_LOAD = 10;

const FreightSearch: React.FC = () => {
  const searchFromStore = useSelector((state: RootState) => state.mainPageSearch);
  const lang = useSelector((state: RootState) => state.settings.lang);
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);

  const prevCargoIdRef = useRef<string | null>(null);
  const initialLoadDoneRef = useRef<boolean>(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const chunkCountRef = useRef(0);
  const initialValues = useRef({
    ...FreightSearchModel.initialValues(),
    ...searchFromStore,
  });

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState<boolean>(false);
  const [data, setData] = useState<FreightSearchData>({ items: [], count: 0, nextCursor: undefined });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null);
  const [isCreatingNewTemplate, setIsCreatingNewTemplate] = useState<boolean>(false);
  const [searchTabsKey, setSearchTabsKey] = useState<number>(0);
  const [searchCoordinates, setSearchCoordinates] = useState<{
    loadingFrom?: { lat: number; ln: number };
    loadingTo?: { lat: number; ln: number };
  } | null>(null);
  const [templates, setTemplates] = useState<{ id: string; notification_sound_enabled?: boolean }[]>([]);

  console.log('data', data);

  const location = useLocation() as { pathname: string; state?: LocationState; search: string };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    entityId: cargoId,
    templateId: urlTemplateId,
    status: urlStatus,
    setEntityId: setCargoId,
    updateQueryParams,
  } = useSearchQueryParams(FREIGHTS.search, 'cargoId');

  const {
    newItemsCounts,
    showRefreshButton,
    setShowRefreshButton,
    clearNewItemsCount,
  } = useNewItemsNotification(authenticated, activeTemplateId, templates);

  const fetchCargoPattern = useCallback(async () => {
    if (location.state && location.state.templateId) {
      await FreightsService.getCargoPattern(location.state.templateId, lang).then((result) => {
        const pattern = FreightSearchModel.formData(result.data.data);
        initialValues.current = {
          ...initialValues.current,
          ...pattern,
        };
      });
    }
  }, [lang, location.state]);

  const fetchData = useCallback(
    async (cursor?: string, templateData?: any, statusFilter?: string | null, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setData({ items: [], count: 0, nextCursor: undefined });
        setHasMore(true);
        chunkCountRef.current = 0;
      }

      const currentStatusFilter = statusFilter !== undefined ? statusFilter : activeStatusFilter;

      // Use templateData if provided, otherwise use form values from initialValues
      const sourceData = templateData || initialValues.current;
      const searchData = FreightSearchModel.searchData(sourceData);

      // Extract search coordinates from form values or template data (only on fresh search)
      if (!append) {
        const newSearchCoordinates: {
          loadingFrom?: { lat: number; ln: number };
          loadingTo?: { lat: number; ln: number };
        } = {};

        // Handle loading address - check both formats
        if (sourceData.loading_addresses?.[0]) {
          // API template format
          const addr = sourceData.loading_addresses[0];
          if (addr.latitude && addr.longitude) {
            newSearchCoordinates.loadingFrom = {
              lat: Number(addr.latitude),
              ln: Number(addr.longitude),
            };
          } else if (addr.location?.latitude && addr.location?.longitude) {
            newSearchCoordinates.loadingFrom = {
              lat: Number(addr.location.latitude),
              ln: Number(addr.location.longitude),
            };
          }
        } else if (sourceData.loading_address) {
          // Form format
          const addr = sourceData.loading_address?.value || sourceData.loading_address;
          if (addr?.location?.latitude && addr?.location?.longitude) {
            newSearchCoordinates.loadingFrom = {
              lat: Number(addr.location.latitude),
              ln: Number(addr.location.longitude),
            };
          }
        }

        // Handle unloading address - check both formats
        if (sourceData.unloading_addresses?.[0]) {
          // API template format
          const addr = sourceData.unloading_addresses[0];
          if (addr.latitude && addr.longitude) {
            newSearchCoordinates.loadingTo = {
              lat: Number(addr.latitude),
              ln: Number(addr.longitude),
            };
          } else if (addr.location?.latitude && addr.location?.longitude) {
            newSearchCoordinates.loadingTo = {
              lat: Number(addr.location.latitude),
              ln: Number(addr.location.longitude),
            };
          }
        } else if (sourceData.unloading_address) {
          // Form format
          const addr = sourceData.unloading_address?.value || sourceData.unloading_address;
          if (addr?.location?.latitude && addr?.location?.longitude) {
            newSearchCoordinates.loadingTo = {
              lat: Number(addr.location.latitude),
              ln: Number(addr.location.longitude),
            };
          }
        }

        setSearchCoordinates(Object.keys(newSearchCoordinates).length > 0 ? newSearchCoordinates : null);
      }

      const statuses = getStatusesForFilter(currentStatusFilter);

      // If status filter is active, don't apply search/template filters - only status
      let requestData: Record<string, any> = {
        limit: ITEMS_PER_LOAD,
        ...(cursor ? { cursor } : {}),
      };

      if (statuses) {
        requestData.statuses = statuses;
      } else {
        // Only apply search filters when no status filter is active
        requestData = {
          ...searchData,
          limit: ITEMS_PER_LOAD,
          ...(cursor ? { cursor } : {}),
        };
      }

      FreightsService.searchCargo({ lang }, requestData)
        .then((result) => {
          if (append) {
            setData((prev) => ({
              items: [...prev.items, ...result.data.items],
              count: result.data.count,
              nextCursor: result.data.nextCursor,
            }));
          } else {
            setData(result.data);
          }
          setHasMore(!!result.data.nextCursor);
          chunkCountRef.current += 1;
        })
        .finally(() => {
          setIsLoading(false);
          setIsLoadingMore(false);
        });
    },
    [lang, activeTemplateId, activeStatusFilter],
  );

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleToggleAdditional = useCallback(() => {
    setIsAdditionalOpen((prev) => !prev);
  }, []);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoading && data.nextCursor) {
      fetchData(data.nextCursor, undefined, activeStatusFilter, true);
    }
  }, [hasMore, isLoadingMore, isLoading, data.nextCursor, fetchData, activeStatusFilter]);

  // Store loadMore in a ref so the observer doesn't need to be recreated on every state change
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const observerRef = useRef<IntersectionObserver | null>(null);

  // Setup IntersectionObserver once
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = tableContainerRef.current;

    if (!sentinel || !container) return;

    // Use container as root on desktop (scrollable), viewport on mobile (page scrolls)
    const root = getComputedStyle(container).overflow === 'visible' ? null : container;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadMoreRef.current();
        }
      },
      {
        root,
        rootMargin: '500px',
        threshold: 0,
      }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, []);

  // Auto-fetch 2nd chunk after initial load
  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    if (!data.nextCursor) return;

    if (chunkCountRef.current === 1) {
      chunkCountRef.current = 2;
      loadMoreRef.current();
    }
  }, [isLoading, isLoadingMore, data.nextCursor]);

  const handleSelectTemplate = useCallback(
    async (templateId: string | null) => {
      // Cancel new template creation when switching to another template
      if (isCreatingNewTemplate) {
        setIsCreatingNewTemplate(false);
      }

      setActiveTemplateId(templateId);
      setShowRefreshButton(false);
      // Clear status filter when selecting a search tab/template
      setActiveStatusFilter(null);
      // Update URL: clear status and set templateId in one call
      updateQueryParams({ status: null, templateId });

      // Clear new items count when selecting template
      if (templateId) {
        clearNewItemsCount(templateId);
      }

      // Fetch data based on selected template
      if (templateId === null) {
        // "All routes" - clear filters and fetch all
        initialValues.current = FreightSearchModel.initialValues();
        setIsExpanded(false);
        // Pass null explicitly to override stale activeStatusFilter from closure
        fetchData(undefined, undefined, null);
      } else {
        // Template selected - fetch full template data from API
        try {
          const response = await FreightsService.getCargoPattern(templateId, lang);
          const fullTemplateData = response.data.data;

          // Convert template data to form format
          const formData = {
            ...FreightSearchModel.initialValues(),
            ...FreightSearchModel.formData(fullTemplateData),
          };

          // Update form initial values with converted data
          initialValues.current = formData;

          // Fetch search results using converted form data (not raw template data)
          // Pass null explicitly to override stale activeStatusFilter from closure
          fetchData(undefined, formData, null);
        } catch (error) {
          console.error('Failed to fetch template data:', error);
        }
      }
    },
    [fetchData, lang, isCreatingNewTemplate, updateQueryParams],
  );

  const handleAddTemplate = useCallback(() => {
    // Create new template inline - expand form and show new tab
    setIsCreatingNewTemplate(true);
    setIsExpanded(true);
    setActiveStatusFilter(null);
    updateQueryParams({ status: null });
    // Reset form to initial values for new template
    initialValues.current = FreightSearchModel.initialValues();
  }, [updateQueryParams]);

  const handleCancelNewTemplate = useCallback(() => {
    setIsCreatingNewTemplate(false);
    setIsExpanded(false);
    initialValues.current = FreightSearchModel.initialValues();
  }, []);

  const handleStatusFilterChange = useCallback(
    (status: string) => {
      setActiveStatusFilter(status);
      setActiveTemplateId(null);
      setIsExpanded(false);
      setIsCreatingNewTemplate(false);
      // Update URL with status and clear templateId in one call to avoid race condition
      updateQueryParams({ status: internalStatusToUrl(status), templateId: null });
      // Fetch data with the new status filter
      fetchData(undefined, undefined, status);
    },
    [fetchData, updateQueryParams],
  );

  const loadData = useCallback(async () => {
    // If there's a templateId in URL, use handleSelectTemplate instead of fetchCargoPattern
    // This handles page refresh and navigation back from map when location.state is lost
    if (urlTemplateId) {
      // If template is already loaded, skip (infinite scroll handles subsequent loads)
      if (urlTemplateId === activeTemplateId) {
        return;
      }
      handleSelectTemplate(urlTemplateId);
      return;
    }

    await fetchCargoPattern();
    fetchData();
  }, [fetchData, fetchCargoPattern, urlTemplateId, handleSelectTemplate, activeTemplateId]);

  useEffect(() => {
    const wasCardJustClosed = prevCargoIdRef.current !== null && cargoId === null;
    const isInitialLoad = !initialLoadDoneRef.current;

    // Update prev cargoId ref
    prevCargoIdRef.current = cargoId;

    // Don't refresh if we just closed a card
    if (wasCardJustClosed) {
      return;
    }

    // Don't refresh if card is open (unless it's initial load)
    if (cargoId && !isInitialLoad) {
      return;
    }

    // Skip initial load if URL has status filter - let the status effect handle it
    if (isInitialLoad && urlStatus) {
      initialLoadDoneRef.current = true;
      return;
    }

    // Initial load
    if (isInitialLoad) {
      initialLoadDoneRef.current = true;
      loadData();
    }
  }, [loadData, cargoId, urlStatus]);

  useEffect(() => {
    return () => {
      dispatch({
        type: SET_MAIN_PAGE_SEARCH,
        payload: {
          loading_address: '',
          loading_address_radius: DEFAULT_ADDRESS_RADIUS,
          unloading_address: '',
          unloading_address_radius: DEFAULT_ADDRESS_RADIUS,
        },
      });
    };
  }, [dispatch]);

  // Handle templateId from URL (when coming from map or direct link)
  useEffect(() => {
    if (urlTemplateId && urlTemplateId !== activeTemplateId) {
      handleSelectTemplate(urlTemplateId);
    }
  }, [urlTemplateId]);

  // Handle status from URL (when coming from direct link)
  useEffect(() => {
    const internalStatus = urlStatusToInternal(urlStatus);
    if (internalStatus && internalStatus !== activeStatusFilter) {
      setActiveStatusFilter(internalStatus);
      setActiveTemplateId(null);
      setIsExpanded(false);
      fetchData(undefined, undefined, internalStatus);
    }
  }, [urlStatus]);

  const handleSubmitForm = useCallback(
    async (values: any, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
      setSubmitting(false);

      // Prepare template data from form values
      const templateData = FreightSearchModel.updateTemplateData(values);

      // If creating a new template, save it
      if (isCreatingNewTemplate) {
        try {
          const response = await FreightsService.saveCargoPattern(templateData);
          const newTemplateId = response.data?.data?.id || response.data?.id;

          // Set the new template as active FIRST, then refresh tabs
          if (newTemplateId) {
            setActiveTemplateId(newTemplateId);
            setIsCreatingNewTemplate(false);
            setSearchTabsKey((prev) => prev + 1); // Force refresh of SearchTabs

            // Fetch full template data and search
            const fullTemplateResponse = await FreightsService.getCargoPattern(newTemplateId, lang);
            const fullTemplateData = fullTemplateResponse.data.data;
            const formData = {
              ...FreightSearchModel.initialValues(),
              ...FreightSearchModel.formData(fullTemplateData),
            };
            initialValues.current = formData;
            fetchData(undefined, formData);
          } else {
            // Fallback if no ID returned
            setIsCreatingNewTemplate(false);
            initialValues.current = { ...values };
            fetchData();
          }
        } catch (error) {
          console.error('Failed to save template:', error);
        }
        return;
      }

      // If editing an existing template, update it
      if (activeTemplateId) {
        try {
          await FreightsService.updateCargoPattern(activeTemplateId, templateData);
          // Refresh tabs to update template name if addresses changed
          setSearchTabsKey((prev) => prev + 1);
        } catch (error) {
          console.error('Failed to update template:', error);
        }
      }

      // Always update initialValues with current form values for search
      initialValues.current = { ...values };

      if (location.pathname.includes(FREIGHTS.search)) {
        fetchData();
      } else {
        navigate(FREIGHTS.search);
      }

      setIsAdditionalOpen(false);
      setIsExpanded(false);
    },
    [navigate, fetchData, location.pathname, isCreatingNewTemplate, activeTemplateId, lang],
  );

  const handleCargoSelect = useCallback(
    (selectedCargoId: string | null) => {
      setCargoId(selectedCargoId);
      setData((prevState) => ({
        ...prevState,
        items: prevState.items.map((item) => ({
          ...item,
          is_seen: item.is_seen || selectedCargoId === item.id,
        })),
      }));
    },
    [setCargoId],
  );

  const updateData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    setShowRefreshButton(false);
    fetchData();
  }, [fetchData]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues.current}
      validationSchema={searchValidateSchema(t)}
      onSubmit={handleSubmitForm}
    >
      {({ resetForm, values, setErrors, setTouched }) => (
        <Form>
          <div className={styles.mainContainer}>
            {/* Search Template Tabs */}
            <SearchTabs
              activeTemplateId={activeTemplateId}
              onSelectTemplate={handleSelectTemplate}
              onAddTemplate={handleAddTemplate}
              newItemsCounts={newItemsCounts}
              isCreatingNew={isCreatingNewTemplate}
              onCancelNewTemplate={handleCancelNewTemplate}
              refreshTrigger={searchTabsKey}
              activeStatusFilter={activeStatusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              onTemplatesLoaded={setTemplates}
            />

            {/* Quick Search Bar */}
            <QuickSearchBar
              isExpanded={isExpanded}
              onToggleExpanded={handleToggleExpanded}
              authenticated={authenticated}
              hideExpandButton={!!activeStatusFilter}
            />

            {/* Expanded Search Form */}
            {isExpanded && (
              <div className={styles.expandedSearchContainer}>
                <SearchForm />
                <SearchOptions isOpen={isAdditionalOpen} />
                <SearchFormButtons
                  isAdditionalOpen={isAdditionalOpen}
                  onToggleAdditional={handleToggleAdditional}
                  onClearForm={() => {
                    resetForm();
                    navigate(FREIGHTS.search);
                    initialValues.current = FreightSearchModel.initialValues();
                  }}
                />
              </div>
            )}

            {/* Refresh Button - shown when active template has new items */}
            {showRefreshButton && (
              <RefreshButton onClick={handleRefresh} translationKey="freightSearch.refresh" />
            )}

            {/* Mobile Status Filters - shown above table on mobile */}
            {authenticated && (
              <MobileStatusFilters
                activeStatusFilter={activeStatusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                translationPrefix="freightSearch"
              />
            )}

            {/* Results Table */}
            <div className={styles.tableContainer} ref={tableContainerRef}>
              <FreightsTable
                data={data}
                updateData={updateData}
                selectedId={cargoId}
                onSelect={handleCargoSelect}
                searchCoordinates={searchCoordinates}
                showStatusColumn={!!activeStatusFilter}
                isLoading={isLoading}
                showAddButton={!activeStatusFilter}
              />
              {/* Sentinel for IntersectionObserver */}
              <div ref={sentinelRef} className={styles.sentinel}>
                {isLoadingMore && (
                  <div className={styles.loadingIndicator}>
                    {t('common.loading', 'Загрузка...')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default FreightSearch;
